import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Image as ImageIcon, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { publicApi, API } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Gold-tier only — host uploads a brand logo AND writes a one-page brief
 * describing what they want their tailored avatar to communicate.
 */
export default function BrandBriefSection({ submissionId, initialBrief = "" }) {
  const [brief, setBrief] = useState(initialBrief);
  const [savingBrief, setSavingBrief] = useState(false);
  const [logo, setLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setBrief(initialBrief || "");
  }, [initialBrief]);

  const loadLogo = async () => {
    if (!submissionId) return;
    try {
      const res = await publicApi.get(`/submissions/${submissionId}/files`);
      const existing = (res.data || []).find((f) => f.category === "brand_logo");
      setLogo(existing || null);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please upload an image file for your logo.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "brand_logo");
      await publicApi.post(`/submissions/${submissionId}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded — thank you.");
      await loadLogo();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveBrief = async () => {
    if (brief.trim().length < 10) {
      toast.error("Please share a little more about your brand (min 10 characters).");
      return;
    }
    setSavingBrief(true);
    try {
      await publicApi.patch(`/submissions/${submissionId}/brand-brief`, {
        avatar_brief: brief.trim(),
      });
      toast.success("Brand brief saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save your brief.");
    } finally {
      setSavingBrief(false);
    }
  };

  return (
    <div
      data-testid="brand-brief-section"
      className="border border-[#E3DED5] bg-white p-6 lg:p-8"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={16} className="text-[#C03028]" />
        <span className="eyebrow">Gold tier · Brand brief</span>
      </div>
      <h3 className="font-serif-display text-2xl text-[#2A1F1A] mt-1">
        A tailored avatar, designed around your story
      </h3>
      <p className="mt-3 text-sm text-[#6E6E70] max-w-2xl leading-relaxed">
        Share your existing logo (if any) and a short, one-page brief describing
        what you&apos;d like your property avatar to communicate. Our studio will
        translate it into a property-branded mark used on your QR welcome card
        and guest landing page.
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Logo upload */}
        <div className="lg:col-span-5">
          <label className="eyebrow block">Your existing logo (optional)</label>
          <label
            data-testid="brand-logo-dropzone"
            className={cn(
              "mt-3 block cursor-pointer border transition-colors overflow-hidden",
              logo
                ? "border-[#C03028]"
                : "border-dashed border-[#E3DED5] hover:border-[#C03028]"
            )}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              disabled={uploading}
              onChange={onLogoUpload}
              data-testid="brand-logo-input"
              className="hidden"
            />
            <div className="aspect-[4/3] bg-[#F2EFE9] relative flex items-center justify-center">
              {logo ? (
                <>
                  <img
                    src={`${API}/files/${logo.id}/download`}
                    alt="Your logo"
                    className="absolute inset-0 w-full h-full object-contain p-6"
                  />
                  <span className="absolute top-2 right-2 bg-[#C03028] text-white w-6 h-6 flex items-center justify-center">
                    <Check size={14} strokeWidth={3} />
                  </span>
                </>
              ) : uploading ? (
                <Loader2 className="animate-spin text-[#C03028]" size={26} />
              ) : (
                <div className="text-center text-[#8E8F90]">
                  <Upload className="mx-auto" size={22} />
                  <div className="text-[10px] uppercase tracking-[0.22em] mt-2">
                    Add your logo
                  </div>
                </div>
              )}
            </div>
            <div className="px-4 py-3 bg-white flex items-center gap-2">
              <ImageIcon size={14} className={logo ? "text-[#C03028]" : "text-[#8E8F90]"} />
              <div className="text-sm text-[#2A1F1A]">
                {logo ? logo.original_filename : "PNG, JPG or SVG"}
              </div>
            </div>
          </label>
        </div>

        {/* Brief textarea */}
        <div className="lg:col-span-7">
          <label className="eyebrow block">
            What should your avatar communicate?
          </label>
          <p className="text-xs text-[#8E8F90] mt-1">
            A short, one-page description — tone, palette, references, anything
            that captures the feeling of your home.
          </p>
          <Textarea
            data-testid="brand-brief-textarea"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={10}
            placeholder="e.g. We want a calm, coastal avatar — soft sands, navy blues, a quiet sailcloth feel. The home is a 1920s villa with a deep family history; we'd like the mark to feel both timeless and personal."
            className="mt-3 bg-transparent border border-[#E3DED5] focus-visible:ring-0 focus-visible:border-[#C03028] rounded-none shadow-none p-4 font-sans-body min-h-[260px]"
          />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-[#8E8F90]">
              {brief.length} characters
            </span>
            <button
              type="button"
              onClick={saveBrief}
              disabled={savingBrief}
              data-testid="brand-brief-save"
              className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm tracking-wide rounded-none"
            >
              {savingBrief && <Loader2 size={14} className="animate-spin" />}
              {savingBrief ? "Saving…" : "Save brand brief"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
